import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';

function formatOptionLabel(value, maxLabelLength) {
  const text = String(value ?? '');
  if (!maxLabelLength || maxLabelLength <= 0 || text.length <= maxLabelLength) {
    return text;
  }
  return `${text.slice(0, maxLabelLength)}…`;
}

export function RoundedSelect({
  value,
  onChange,
  options,
  placeholder,
  disabled,
  className,
  searchable = false,
  searchPlaceholder = 'Search...',
  menuMaxHeight,
  maxLabelLength,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [menuPos, setMenuPos] = useState(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const instanceIdRef = useRef(`rs_${Math.random().toString(36).slice(2)}`);

  const selected = useMemo(() => options.find((opt) => opt.value === value) || null, [options, value]);
  const label = selected
    ? formatOptionLabel(selected.label, maxLabelLength)
    : placeholder || 'Select';

  const computeMenuPos = () => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return null;

    const gap = 8;
    const viewportPadding = 8;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
    const width = Math.max(160, rect.width);
    const estimatedMenuHeight = menuMaxHeight || 320;

    const spaceBelow = viewportHeight - rect.bottom - viewportPadding;
    const spaceAbove = rect.top - viewportPadding;
    const openAbove = spaceBelow < Math.min(180, estimatedMenuHeight) && spaceAbove > spaceBelow;
    const maxAvailableHeight = Math.max(120, openAbove ? spaceAbove - gap : spaceBelow - gap);
    const maxHeight = Math.min(estimatedMenuHeight, Math.floor(maxAvailableHeight));

    const left = Math.max(
      viewportPadding,
      Math.min(rect.left, viewportWidth - width - viewportPadding)
    );

    if (openAbove) {
      // Anchor the menu bottom to the trigger top so short lists don't float too high.
      return {
        openAbove: true,
        bottom: viewportHeight - rect.top + gap,
        left,
        width,
        maxHeight,
      };
    }

    return {
      openAbove: false,
      top: rect.bottom + gap,
      left,
      width,
      maxHeight,
    };
  };

  useEffect(() => {
    const onAnotherSelectOpened = (event) => {
      if (!event?.detail?.id) return;
      if (event.detail.id === instanceIdRef.current) return;
      setOpen(false);
    };
    document.addEventListener('rounded-select:open', onAnotherSelectOpened);
    return () => {
      document.removeEventListener('rounded-select:open', onAnotherSelectOpened);
    };
  }, []);

  useEffect(() => {
    if (!open) {
      setQuery('');
      setMenuPos(null);
      return undefined;
    }

    const recomputeMenuPlacement = () => {
      const next = computeMenuPos();
      if (next) setMenuPos(next);
    };

    recomputeMenuPlacement();
    window.addEventListener('resize', recomputeMenuPlacement);
    window.addEventListener('scroll', recomputeMenuPlacement, true);
    return () => {
      window.removeEventListener('resize', recomputeMenuPlacement);
      window.removeEventListener('scroll', recomputeMenuPlacement, true);
    };
  }, [open, menuMaxHeight]);

  useEffect(() => {
    if (!open) return undefined;

    const onDocMouseDown = (event) => {
      if (triggerRef.current?.contains(event.target)) return;
      if (menuRef.current?.contains(event.target)) return;
      setOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const filteredOptions = useMemo(() => {
    if (!searchable) return options;
    const normalized = query.trim().toLowerCase();
    if (!normalized) return options;
    return options.filter((opt) => String(opt.label || '').toLowerCase().includes(normalized));
  }, [options, query, searchable]);

  const toggleOpen = () => {
    if (disabled) return;
    if (open) {
      setOpen(false);
      return;
    }
    const nextPos = computeMenuPos();
    setMenuPos(nextPos);
    setOpen(true);
    document.dispatchEvent(
      new CustomEvent('rounded-select:open', { detail: { id: instanceIdRef.current } })
    );
  };

  return (
    <div className={className ? `rounded-select ${className}` : 'rounded-select'}>
      <button
        ref={triggerRef}
        type="button"
        className="rounded-select-trigger"
        onClick={toggleOpen}
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={disabled}
      >
        <span
          className={selected ? 'rounded-select-label' : 'rounded-select-label placeholder'}
          title={selected ? String(selected.label || '') : undefined}
        >
          {label}
        </span>
        <span className={open ? 'rounded-select-caret open' : 'rounded-select-caret'} aria-hidden="true" />
      </button>

      {typeof document !== 'undefined' &&
        createPortal(
          <AnimatePresence>
            {open && !disabled && menuPos && (
              <motion.div
                ref={menuRef}
                className="rounded-select-menu rounded-select-menu-portal"
                role="listbox"
                style={{
                  position: 'fixed',
                  top: menuPos.openAbove ? 'auto' : menuPos.top,
                  bottom: menuPos.openAbove ? menuPos.bottom : 'auto',
                  left: menuPos.left,
                  width: menuPos.width,
                  minWidth: menuPos.width,
                  maxWidth: menuPos.width,
                  maxHeight: menuPos.maxHeight,
                  overflowY: 'auto',
                  zIndex: 3000,
                }}
                initial={{ opacity: 0, y: 6, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 6, scale: 0.98 }}
                transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
              >
                {searchable && (
                  <div className="rounded-select-search">
                    <input
                      type="text"
                      value={query}
                      onChange={(event) => setQuery(event.target.value)}
                      placeholder={searchPlaceholder}
                    />
                  </div>
                )}
                {filteredOptions.map((opt) => (
                  <button
                    key={String(opt.value)}
                    type="button"
                    className={opt.value === value ? 'rounded-select-option selected' : 'rounded-select-option'}
                    title={String(opt.label || '')}
                    onClick={() => {
                      onChange(opt.value);
                      setOpen(false);
                    }}
                    role="option"
                    aria-selected={opt.value === value}
                  >
                    {formatOptionLabel(opt.label, maxLabelLength)}
                  </button>
                ))}
                {filteredOptions.length === 0 && <div className="rounded-select-empty">No results</div>}
              </motion.div>
            )}
          </AnimatePresence>,
          document.body
        )}
    </div>
  );
}
